import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import { useBankConnectFactory, useBankConnectWithBasiq } from "bankconnect";
import type { BankStatementsGroup } from "bankconnect";
import { useCallback, useEffect, useRef, useState } from "react";
import { AuthCallbackPath, useErrorModal, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import {
  BANK_CONNECT_API,
  BANK_CONNECT_SOURCES_API,
  CONNECT_APP_ROUTE_PATHS,
  FUNDING_APPLICATION_CONFIGURATION_COMPLETION_STATUS_API,
} from "../../../components/constants";
import { useBankStatementUploadManager } from "../../../components/additional-information-requests/hooks/bank-statement-upload-manager";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { useBankStatementSubmitMutation } from "../../../queries/bank-connect/bank-statement.mutations";
import {
  groupedBankingSourcesQueryOptions,
  useBankConnectProps,
  useBankingSources,
  useSyncAccountGroups,
} from "../../../queries/bank-connect/queries";
import { getFundingApplicationStagesQueryOptions } from "../../../queries/funding-application-configuration/queries";

import { useCountrySelectionForm } from "./use-country-selection-form";

const countStatementFiles = (groups: BankStatementsGroup[] | null | undefined): number => {
  if (!groups?.length) {
    return 0;
  }
  return groups.reduce((sum, g) => sum + (g.numberOfFiles ?? g.previouslyUploadedStatements?.length ?? 0), 0);
};

type UseBankControllerProps = {
  // Whether the customer is currently on BankingAccountsPage (the new UI), as opposed to
  // BankingNextPage - both share this hook, but only the new page's connections should sync into
  // account groups. Gating this on the feature flag instead would also sync connections for
  // customers the flag has simply rolled out to but who are still shown the old page.
  isNewAccountsPageActive?: boolean;
};

export const useBankController = ({ isNewAccountsPageActive = false }: UseBankControllerProps = {}) => {
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const {
    data: { bankConnectProps },
  } = useBankConnectProps(customerDomain);
  const { data: bankingSources } = useBankingSources(customerDomain);
  const queryClient = useQueryClient();
  const { openErrorModal } = useErrorModal();
  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.BANKING });
  const { navigate } = useSpaNavigate("FundingApp");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitBankStatements = useBankStatementSubmitMutation(customerDomain);
  const { mutate: syncAccountGroups } = useSyncAccountGroups(customerDomain);
  const { data: fundingApplicationStages } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));
  const requireConnection = fundingApplicationStages?.banking?.requireConnection === true;
  const requiredStatementMonths = requireConnection
    ? 12
    : fundingApplicationStages?.banking?.requiredStatementMonths ?? 6;

  const invalidateBankSources = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [BANK_CONNECT_SOURCES_API.replace(":customerSlug", customerDomain), customerDomain],
    });
    queryClient.invalidateQueries({
      queryKey: groupedBankingSourcesQueryOptions(customerDomain).queryKey,
    });
  }, [queryClient, customerDomain]);

  // Catches up completed mocked connections and those created outside this page's flow.
  // Skipped when there are no connections at all - nothing to catch up.
  const hasSyncedOnMountRef = useRef(false);
  useEffect(() => {
    if (isNewAccountsPageActive && !hasSyncedOnMountRef.current && !!bankingSources?.bankingDataSources.length) {
      hasSyncedOnMountRef.current = true;
      syncAccountGroups(undefined, { onSettled: invalidateBankSources });
    }
  }, [isNewAccountsPageActive, syncAccountGroups, invalidateBankSources, bankingSources]);

  const { ready, useBankConnect } = useBankConnectFactory(bankConnectProps, {
    customerSlug: customerDomain,
    basiqNextPath: AuthCallbackPath.FUNDING_APPLICATION_BANKING,
    onConnectionCallback() {
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_API.replace(":customerSlug", customerDomain), customerDomain],
      });
      queryClient.invalidateQueries({
        queryKey: [FUNDING_APPLICATION_CONFIGURATION_COMPLETION_STATUS_API.replace(":customerSlug", customerDomain)],
      });
      if (isNewAccountsPageActive) {
        syncAccountGroups(undefined, { onSettled: invalidateBankSources });
      } else {
        invalidateBankSources();
      }
    },
  });

  const { startConnect, bankConnections, plaidLinkElement } = useBankConnect();
  const { basiqClient } = useBankConnectWithBasiq({
    ...bankConnectProps.basiqConnectProps,
    customerSlug: customerDomain,
    nextPath: AuthCallbackPath.FUNDING_APPLICATION_BANKING,
  });

  const shouldConfirmAccountSelection =
    bankConnectProps.basiqConnectProps.needsAccountSelection &&
    bankConnectProps.bankSettings.initialBankingData.bankingDataSources.length > 0;
  const confirmAccountsBankConnections = bankConnections.filter((c) => !c.accountsSelected);

  const hasBankingConnections = !!bankingSources?.bankingDataSources.length;

  const {
    countryForm,
    countryOptions,
    defaultCountry,
    providerName,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
  } = useCountrySelectionForm({
    bankConnectCountries: bankConnectProps.countryOptions,
    countryOfIncorporation,
    requireConnection,
    startConnect,
  });

  const bankingStatementsGroups = bankingSources.bankingStatementsGroups ?? null;

  const useUploadManager = useBankStatementUploadManager(customerDomain);
  const fileUploadProps = useUploadManager();

  const completedUploadObjectKeys = fileUploadProps.uploadsState?.uploads
    .filter((u) => u.status === "complete" && u.uploadedFile?.objectKey)
    .map((u) => u.uploadedFile?.objectKey)
    .filter((key): key is string => !!key);

  const uploadsInProgress =
    !!fileUploadProps.uploadsState?.uploadsStarted && !fileUploadProps.uploadsState?.uploadsComplete;

  const statementCount = countStatementFiles(bankingStatementsGroups);
  const hasStatements = statementCount > 0;

  const stepCompleted = hasBankingConnections || (!requireConnection && hasStatements);
  const isUkIncorporated = countryOfIncorporation === "GB";

  const handleSubmitStatements = async (bankName: string) => {
    try {
      await submitBankStatements.mutateAsync({
        object_keys: completedUploadObjectKeys,
        group: bankName,
      });
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_SOURCES_API.replace(":customerSlug", customerDomain), customerDomain],
      });
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_API.replace(":customerSlug", customerDomain), customerDomain],
      });
    } catch (e: any) {
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "bank-controller: submit statements error" });
      throw e;
    }
  };

  const handleNext = async (onTerminalStep?: () => void | Promise<void>) => {
    if (!navigationContext.nextPath && !onTerminalStep) {
      throw new Error("bank-controller: nextPath is not defined");
    }
    setIsSubmitting(true);
    try {
      if (navigationContext.nextPath) {
        await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
      } else {
        await onTerminalStep?.();
        setIsSubmitting(false);
      }
    } catch (e: any) {
      setIsSubmitting(false);
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "bank-controller: submit error" });
    }
  };

  const handleBack = () => {
    if (navigationContext.previousPath) {
      navigationContext.navigate(navigationContext.previousPath);
    } else {
      navigate(CONNECT_APP_ROUTE_PATHS.BUSINESS_MODEL);
    }
  };

  return {
    // Bank connect state
    ready,
    bankConnectProps,
    requireConnection,
    requiredStatementMonths,
    // Basiq account selection
    shouldConfirmAccountSelection,
    confirmAccountsBankConnections,
    // Navigation state
    isSubmitting,
    stepCompleted,
    isUkIncorporated,
    uploadsInProgress,
    // Connection panel
    providerName,
    defaultCountry,
    countryOptions,
    countryForm,
    plaidLinkElement,
    bankingConnections: bankingSources.bankingDataSources,
    hasBankingConnections: !!hasBankingConnections,
    basiqClient,
    isConnectionUnsupportedCountry,
    isConnecting,
    // Upload panel
    bankingStatementsGroups,
    statementCount,
    fileUploadProps,
    // Handlers
    handleConnect,
    handleSubmitStatements,
    handleNext,
    handleBack,
  };
};
